import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, apiReponseError, apiReponseErrorIncludes } from '@shared';
import { AdvisorSponsorship } from '@shared/types/AdvisorSponsorship';
import {
  postCancellationForSponsorshipsBySponsorshipIdRequest,
  postCancellationForSponsorshipsBySponsorshipIdResponse,
  postCancellationForSponsorshipsBySponsorshipIdFailure,
} from './sponsorshipModel';

const advisorAdminPortalSlice = createSlice({
  name: 'advisorAdminPortal',
  initialState: initialState.advisorAdminPortal,
  reducers: {
    postAdvisorFieldUpdateByAdvisorIdRequest: (
      state,
      _action: PayloadAction<AdvisorFieldUpdateProps>,
    ) => {
      state.status = 'loading';
      state.errorMessage = null;
      state.successMessage = null;
    },
    postAdvisorFieldUpdateByAdvisorIdResponse: (state, action: PayloadAction<string>) => {
      if (action.payload === 'Advisor not found') {
        state.errorMessage = action.payload;
        state.status = 'error';
      } else if (action.payload === 'Invalid email update method') {
        state.errorMessage =
          "Can't update advisor email, advisor is using Google or Microsoft as their login method.";
        state.status = 'error';
      } else {
        state.status = 'success';
        state.successMessage = action.payload;
      }
    },
    postAdvisorFieldUpdateByAdvisorIdFailure: (state, action: PayloadAction<ApiExError>) => {
      if (action?.payload?.statusCode === '409') {
        state.errorMessage = 'An advisor with this email already exists!';
      } else {
        state.errorMessage = 'An unknown error occurred';
      }
      state.status = 'error';
    },
    resetAdvisorAdminPortal: state => {
      state.status = null;
      state.errorMessage = null;
      state.successMessage = null;
      state.advisorFetchStatus = null;
      state.advisorFetchError = null;
      state.selectedAdvisor = null;
    },
    postAdvisorForAdminSearchRequest: (
      state,
      _action: PayloadAction<{ advisorId?: string; advisorEmail?: string }>,
    ) => {
      state.advisorFetchStatus = 'loading';
      state.advisorFetchError = null;
      state.selectedAdvisor = null;
    },
    postAdvisorForAdminSearchResponse: (state, action: PayloadAction<Advisor>) => {
      state.advisorFetchStatus = 'success';
      state.selectedAdvisor = action.payload;
      state.advisorFetchError = null;
    },
    postAdvisorForAdminSearchFailure: (state, _action: PayloadAction<ApiExError>) => {
      state.advisorFetchStatus = 'error';
      state.selectedAdvisor = null;
      state.advisorFetchError = 'Advisor not found';
    },
    postAdvisorReportingAttributeByAdvisorIdRequest: (
      state,
      _action: PayloadAction<AdvisorReportingAttributeProps>,
    ) => {
      state.status = 'loading';
      state.errorMessage = null;
      state.successMessage = null;
    },
    postAdvisorReportingAttributeByAdvisorIdFailure: (state, action: PayloadAction<ApiExError>) => {
      state.status = 'error';
      if (apiReponseErrorIncludes(action.payload, 'advisor not found')) {
        state.errorMessage = 'Advisor ID not found';
      } else if (apiReponseErrorIncludes(action.payload, 'create failed')) {
        state.errorMessage = 'Attribute already exists. Please use the update or delete action.';
      } else if (apiReponseErrorIncludes(action.payload, 'update failed')) {
        const errorMessage = apiReponseError(action.payload);
        state.errorMessage = errorMessage;
      } else if (apiReponseErrorIncludes(action.payload, 'delete failed')) {
        state.errorMessage = 'Delete failed. Attribute does not exist.';
      } else {
        state.errorMessage = 'An unknown error occurred';
      }
    },
    postAdvisorReportingAttributeByAdvisorIdResponse: (state, action: PayloadAction<string>) => {
      state.status = 'success';
      state.successMessage = action.payload;
    },
    postInvitationForSponsorshipsBySponsorshipIdRequest: (
      state,
      _action: PayloadAction<{ sponsorshipId: string; advisorId: string }>,
    ) => {
      state.status = 'loading';
      state.errorMessage = null;
      state.successMessage = null;
    },
    postInvitationForSponsorshipsBySponsorshipIdResponse: (
      state,
      action: PayloadAction<AdvisorSponsorship>,
    ) => {
      state.status = 'success';
      state.successMessage = `Successfully invited advisor (ID: ${action.payload.advisorId}) to sponsorship (ID: ${action.payload.sponsorshipId}). An email notification has been sent.`;
    },
    postInvitationForSponsorshipsBySponsorshipIdFailure: (
      state,
      action: PayloadAction<ApiExError>,
    ) => {
      state.status = 'error';

      if (apiReponseErrorIncludes(action.payload, 'Advisor with ID')) {
        state.errorMessage = 'Advisor ID not found.';
      } else if (apiReponseErrorIncludes(action.payload, 'Sponsorship with ID')) {
        state.errorMessage = 'Sponsorship ID not found.';
      } else if (
        apiReponseErrorIncludes(action.payload, 'already either invited or actively sponsored')
      ) {
        state.errorMessage =
          'Advisor is already invited or actively sponsored for this sponsorship.';
      } else {
        state.errorMessage = 'An unknown error occurred';
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(postCancellationForSponsorshipsBySponsorshipIdRequest, (state, _action) => {
        state.status = 'loading';
        state.errorMessage = null;
        state.successMessage = null;
      })
      .addCase(postCancellationForSponsorshipsBySponsorshipIdResponse, (state, action) => {
        state.status = 'success';
        state.successMessage = `Successfully cancelled sponsorship (ID: ${action.payload.sponsorshipId}) for advisor (ID: ${action.payload.advisorId}). An email notification has been sent.`;
      })
      .addCase(postCancellationForSponsorshipsBySponsorshipIdFailure, (state, action) => {
        state.status = 'error';
        if (apiReponseErrorIncludes(action.payload, 'Advisor with ID')) {
          state.errorMessage = 'Advisor ID not found.';
        } else if (apiReponseErrorIncludes(action.payload, 'Sponsorship with ID')) {
          state.errorMessage = 'Sponsorship ID not found.';
        } else if (apiReponseErrorIncludes(action.payload, 'No advisor sponsorship found.')) {
          state.errorMessage =
            'No existing sponsorship to cancel for given advisor and sponsorship IDs.';
        } else if (
          apiReponseErrorIncludes(action.payload, 'Advisor sponsorship already inactive')
        ) {
          state.errorMessage = 'Advisor sponsorship non-existing or already cancelled/inactive.';
        } else {
          state.errorMessage = 'An unknown error occurred.';
        }
      });
  },
});

export const {
  postAdvisorFieldUpdateByAdvisorIdRequest,
  postAdvisorFieldUpdateByAdvisorIdResponse,
  postAdvisorFieldUpdateByAdvisorIdFailure,
  postAdvisorReportingAttributeByAdvisorIdRequest,
  postAdvisorReportingAttributeByAdvisorIdResponse,
  postAdvisorReportingAttributeByAdvisorIdFailure,
  postInvitationForSponsorshipsBySponsorshipIdRequest,
  postInvitationForSponsorshipsBySponsorshipIdResponse,
  postInvitationForSponsorshipsBySponsorshipIdFailure,
  postAdvisorForAdminSearchRequest,
  postAdvisorForAdminSearchResponse,
  postAdvisorForAdminSearchFailure,
  resetAdvisorAdminPortal,
} = advisorAdminPortalSlice.actions;

export const advisorAdminPortalReducer = advisorAdminPortalSlice.reducer;
